import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  useExpense,
  useCreateExpense,
  useUpdateExpense,
  useAssetCategories,
  useAssetVendors,
} from '../../../services/httpServices/queries';
import { ExpensesLayout } from './ExpensesLayout';
import { usePermissions } from '../../../hooks/usePermissions';
import { AttachmentUploadField } from '../../../components/ui/AttachmentUploadField';
import { ValidatedNumberInput } from '../../../components/ui/validated-number-input';
import { DatePicker } from '../../../components/ui/DatePicker';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import {
  Loader2,
  AlertCircle,
  Save,
  ArrowLeft,
  Lock,
  Info,
  Wallet,
  CreditCard,
  FileText,
  Paperclip,
  RefreshCw,
  CheckCircle,
  type LucideIcon,
} from 'lucide-react';
import type { ExpenseFrequency, ExpenseStatus, ExpenseType } from '../../../types/expense';

const inputClass =
  'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text placeholder:text-text-muted/70 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60';
const labelClass = 'mb-1.5 block text-sm font-medium text-text';

const CURRENCY = '৳';
const fmtMoney = (value: number) =>
  `${CURRENCY}${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'insurance', label: 'Insurance' },
];

const FREQUENCIES: { value: ExpenseFrequency; label: string }[] = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'YEARLY', label: 'Yearly' },
];

const today = () => new Date().toISOString().slice(0, 10);

function SectionCard({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-surface shadow-sm">
      <div className="flex items-center gap-3 border-b border-border px-4 py-3.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon size={18} />
        </span>
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-text">{title}</h2>
          {description && <p className="text-xs text-text-muted">{description}</p>}
        </div>
      </div>
      <div className="space-y-4 p-4">{children}</div>
    </section>
  );
}

function Field({
  label,
  required,
  children,
  className,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className={labelClass}>
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </label>
      {children}
    </div>
  );
}

export function ExpenseForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { data: expenseData, isLoading: expenseLoading, error: expenseError } = useExpense(id);
  const expense = expenseData?.data;
  const { data: categoriesData } = useAssetCategories(undefined, true, 'EXPENSE');
  const { data: vendorsData } = useAssetVendors(undefined, true);

  const categories = categoriesData?.data || [];
  const vendors = vendorsData?.data || [];

  const createExpense = useCreateExpense();
  const updateExpense = useUpdateExpense();
  const isSubmitting = createExpense.isPending || updateExpense.isPending;

  // The expense date drives the ledger/cashbook entry, so only roles granted
  // `expenses:backdate` (Roles → Permissions) may choose it. Everyone else gets
  // "now" from the server on create, and an existing date can't be changed.
  const { hasPermission } = usePermissions();
  const canEditExpenseDate = hasPermission('expenses:backdate');

  const [initializedId, setInitializedId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [amount, setAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState(today());
  const [expenseType, setExpenseType] = useState<ExpenseType>('ONE_TIME');
  const [frequency, setFrequency] = useState<ExpenseFrequency>('MONTHLY');
  const [status, setStatus] = useState<ExpenseStatus>('PAID');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!expense || initializedId === expense.id) return;
    setTitle(expense.title || '');
    setCategoryId(expense.categoryId || '');
    setAmount(String(expense.amount ?? ''));
    setExpenseDate(expense.expenseDate ? expense.expenseDate.slice(0, 10) : today());
    setExpenseType(expense.expenseType || 'ONE_TIME');
    setFrequency(expense.frequency || 'MONTHLY');
    setStatus(expense.status || 'PAID');
    setPaymentMethod(expense.paymentMethod || 'cash');
    setPaymentReference(expense.paymentReference || '');
    setVendorId(expense.vendorId || '');
    setDescription(expense.description || '');
    setNotes(expense.notes || '');
    setAttachmentUrl(expense.attachmentUrl || '');
    setInitializedId(expense.id);
  }, [expense, initializedId]);

  const lockedFinancials = isEdit && Boolean(expense?.cashbookEntryId);
  const isRecurring = expenseType === 'RECURRING';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!title.trim()) return setFormError('Title is required');
    if (amount === '' || Number(amount) < 0) return setFormError('A valid amount is required');
    if (canEditExpenseDate && !expenseDate) return setFormError('Expense date is required');
    if (isRecurring && !frequency) return setFormError('Choose a frequency for a recurring expense');

    const payload = {
      title: title.trim(),
      categoryId: categoryId || undefined,
      amount: Number(amount),
      // Omitted for roles without the permission: the server stamps "now" on
      // create and keeps the stored date on update.
      expenseDate: canEditExpenseDate ? expenseDate : undefined,
      expenseType,
      frequency: isRecurring ? frequency : undefined,
      paymentMethod,
      paymentReference: paymentReference.trim() || undefined,
      status,
      vendorId: vendorId || undefined,
      description: description.trim() || undefined,
      notes: notes.trim() || undefined,
      attachmentUrl: attachmentUrl.trim() || undefined,
    };

    const onSuccess = () => navigate('/billing/expenses');
    const onError = (err: any) =>
      setFormError(
        err?.details?.map((d: any) => d.message).join(', ') || err?.message || 'Failed to save expense',
      );

    if (isEdit && id) {
      updateExpense.mutate({ id, data: payload }, { onSuccess, onError });
    } else {
      createExpense.mutate(payload, { onSuccess, onError });
    }
  };

  if (isEdit && expenseLoading) {
    return (
      <ExpensesLayout title="Edit Expense" helpKey="/billing/expenses/form">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </ExpensesLayout>
    );
  }

  if (isEdit && (expenseError || !expense)) {
    return (
      <ExpensesLayout title="Edit Expense" helpKey="/billing/expenses/form">
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load expense</span>
        </div>
      </ExpensesLayout>
    );
  }

  return (
    <ExpensesLayout
      title={isEdit ? 'Edit Expense' : 'Record Expense'}
      description={
        isEdit
          ? `${expense?.expenseNumber} — update the expense details`
          : 'Record a daily one-off cost or a fixed/recurring expense'
      }
      helpKey="/billing/expenses/form"
      action={
        <Link
          to="/billing/expenses"
          className="flex items-center gap-2 rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-text transition-colors hover:bg-background"
        >
          <ArrowLeft size={16} />
          Back
        </Link>
      }
    >
      {formError && (
        <div className="flex items-start gap-2 rounded-lg border border-danger/20 bg-danger/10 p-4 text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{formError}</span>
        </div>
      )}

      {lockedFinancials && (
        <div className="flex items-start gap-2 rounded-lg border border-warning/20 bg-warning/10 p-4 text-sm text-text">
          <Lock size={16} className="mt-0.5 shrink-0 text-warning" />
          <span>
            This expense already has a cashbook entry. Amount, payment method and status are locked to keep the
            cashbook in sync. Delete and re-record the expense to change them.
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* Main column — what the expense is */}
          <div className="space-y-5 lg:col-span-2">
            <SectionCard
              icon={Wallet}
              title="Expense details"
              description="What was spent, when and who it belongs to."
            >
              <Field label="Title" required className="sm:col-span-2">
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Monthly office rent"
                  maxLength={255}
                  className={inputClass}
                />
              </Field>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Category">
                  <select
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">— Uncategorised —</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Amount" required>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-text-muted">
                      {CURRENCY}
                    </span>
                    <ValidatedNumberInput
                      value={amount}
                      onChange={(v) => setAmount(String(v))}
                      disabled={lockedFinancials}
                      placeholder="0.00"
                      className={cn(inputClass, 'pl-8')}
                    />
                  </div>
                </Field>

                <Field label="Expense Date" required={canEditExpenseDate}>
                  <DatePicker
                    value={expenseDate}
                    onChange={setExpenseDate}
                    disabled={!canEditExpenseDate}
                    clearable={canEditExpenseDate}
                    required={canEditExpenseDate}
                  />
                  <p className="mt-1 flex items-start gap-1 text-[0.625rem] leading-4 text-text-muted">
                    <Info size={10} className="mt-0.5 shrink-0" />
                    {canEditExpenseDate
                      ? 'The date this expense is posted on. It also dates the cashbook entry.'
                      : 'Recorded automatically when you save. Requires the “Backdate Expense” permission to change.'}
                  </p>
                </Field>

                <Field label="Type">
                  <select
                    value={expenseType}
                    onChange={(e) => setExpenseType(e.target.value as ExpenseType)}
                    className={inputClass}
                  >
                    <option value="ONE_TIME">One-time (daily / occasional)</option>
                    <option value="RECURRING">Recurring (fixed / repeat)</option>
                  </select>
                </Field>

                {isRecurring && (
                  <Field label="Frequency">
                    <select
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value as ExpenseFrequency)}
                      className={inputClass}
                    >
                      {FREQUENCIES.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}

                <Field label="Vendor / Payee">
                  <select value={vendorId} onChange={(e) => setVendorId(e.target.value)} className={inputClass}>
                    <option value="">— None —</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </SectionCard>

            <SectionCard icon={FileText} title="Description & notes" description="Optional context for this expense.">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Description">
                  <textarea
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What was this expense for?"
                    className={cn(inputClass, 'resize-none')}
                  />
                </Field>
                <Field label="Internal Notes">
                  <textarea
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notes visible to your team only"
                    className={cn(inputClass, 'resize-none')}
                  />
                </Field>
              </div>
            </SectionCard>
          </div>

          {/* Sidebar — how it was paid and supporting documents */}
          <div className="space-y-5">
            <div className="rounded-xl border border-border bg-gradient-to-br from-primary/10 via-surface to-surface p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Expense amount</p>
              <p className="mt-1 text-3xl font-bold text-text">{fmtMoney(Number(amount) || 0)}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-surface/80 px-2.5 py-1 text-xs font-medium text-text ring-1 ring-border">
                  <RefreshCw size={12} />
                  {isRecurring ? `Recurring · ${frequency}` : 'One-time'}
                </span>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1',
                    status === 'PAID'
                      ? 'bg-success/10 text-success ring-success/20'
                      : 'bg-warning/10 text-warning ring-warning/20',
                  )}
                >
                  <CheckCircle size={12} />
                  {status === 'PAID' ? 'Paid' : 'Pending'}
                </span>
              </div>
            </div>

            <SectionCard icon={CreditCard} title="Payment" description="How and when it was settled.">
              <Field label="Status">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as ExpenseStatus)}
                  disabled={lockedFinancials}
                  className={inputClass}
                >
                  <option value="PAID">Paid</option>
                  <option value="PENDING">Pending</option>
                </select>
              </Field>

              <Field label="Payment Method">
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  disabled={lockedFinancials}
                  className={inputClass}
                >
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Reference">
                <input
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Cheque / transaction no."
                  maxLength={100}
                  disabled={lockedFinancials}
                  className={inputClass}
                />
              </Field>

              <div className="flex items-start gap-2 text-xs text-text-muted">
                <Info size={14} className="mt-0.5 shrink-0" />
                <span>
                  A paid cash expense records a matching cash-out entry in the Cashbook. Non-cash payments are
                  tracked here only.
                </span>
              </div>
            </SectionCard>

            <SectionCard
              icon={Paperclip}
              title="Receipt / Attachment"
              description="Attach a scan or photo of the receipt."
            >
              <AttachmentUploadField
                value={attachmentUrl}
                onChange={setAttachmentUrl}
                entityType="general"
                entityId="expense-receipt"
                description="Expense receipt"
                helperText="Images and PDF files, up to 50 MB."
              />
            </SectionCard>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 rounded-xl border border-border bg-surface px-4 py-3 shadow-sm">
          <Button type="button" variant="ghost" onClick={() => navigate('/billing/expenses')}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting} className="gap-2">
            {!isSubmitting && <Save size={16} />}
            {isEdit ? 'Save Changes' : 'Record Expense'}
          </Button>
        </div>
      </form>
    </ExpensesLayout>
  );
}
